import type { ReactNode } from 'react';
import type { RentalSearchValue } from '../models.js';
import { RentalDatesField, type RentalDatesCopy } from './rental_dates_field.js';
import type { FieldGuide } from '../../shared/guided_flow.js';
import { FieldColumn } from './rental_field_column.js';

/**
 * A coluna do QUANDO da locação: um campo só, com os dias e as horas no mesmo
 * painel (#4513, o desenho do Figma). O valor da barra segue sendo a janela
 * (`pickupAt`/`dropoffAt`), e o painel devolve a janela inteira ao confirmar.
 */
export function RentalWhenFields({
  value,
  copy,
  disabled,
  sheet,
  pickupFloor,
  datesGuide,
  datesSearch = false,
  onChange,
  onDatesConfirmed,
}: {
  readonly value: RentalSearchValue;
  readonly copy: RentalDatesCopy & { readonly backLabel: string };
  readonly disabled: boolean;
  readonly sheet: { readonly backLabel: string } | undefined;
  /** O primeiro instante escolhível para a retirada (`YYYY-MM-DDTHH:mm`). */
  readonly pickupFloor: string;
  /** O preenchimento guiado no campo (ver `RentalDatesField.guide`). */
  readonly datesGuide?: FieldGuide;
  /** Confirmar a janela também busca: o Confirmar diz isso no rótulo. */
  readonly datesSearch?: boolean;
  readonly onChange: (value: RentalSearchValue) => void;
  /** A janela foi confirmada: o valor recém-composto, para quem encadeia o
   *  próximo campo ou a busca. `onChange` já saiu com ele. */
  readonly onDatesConfirmed?: (value: RentalSearchValue) => void;
}): ReactNode {
  return (
    <FieldColumn label={copy.datesLabel} field="dates">
      <RentalDatesField
        value={{
          ...(value.pickupAt ? { pickupAt: value.pickupAt } : {}),
          ...(value.dropoffAt ? { dropoffAt: value.dropoffAt } : {}),
        }}
        copy={copy}
        pickupFloor={pickupFloor}
        disabled={disabled}
        sheet={sheet}
        {...(datesGuide ? { guide: datesGuide } : {})}
        searches={datesSearch}
        onConfirm={(window) => {
          const next = { ...value, pickupAt: window.pickupAt, dropoffAt: window.dropoffAt };
          onChange(next);
          onDatesConfirmed?.(next);
        }}
      />
    </FieldColumn>
  );
}
